import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import {useLocation, useNavigate, useParams} from 'react-router';
import {useSession} from '../../../firebase/UserProvider';
import SourcesFileViewerContent from '../../products/reportEditorPage/leftPanel/components/SourcesFileViewerContent';
import {
    deleteCollectionMaterial,
    getCollectionMaterial,
    modifyCollectionMaterial,
    submitCollectionMaterial,
    updateCollectionMaterial,
} from '../collectionTaskApi';
import {
    CM_COLLECTION_METHOD_OPTIONS,
    CM_TAG_ROLES,
    collectionTaskPath,
    hasAnyRole,
    sanitizeReturnTo,
} from '../collectionTaskPayload';
import EntityTagsSection from '../../collection-requests/tags/EntityTagsSection';

function SourceKindIcon({sourceKind}) {
    if (sourceKind === 'url') return <LinkOutlinedIcon fontSize="small"/>;
    if (sourceKind === 'raw') return <NotesOutlinedIcon fontSize="small"/>;
    return <InsertDriveFileOutlinedIcon fontSize="small"/>;
}

function sourceKindLabel(sourceKind) {
    if (sourceKind === 'url') return 'URL';
    if (sourceKind === 'raw') return 'Raw text';
    if (sourceKind === 'file') return 'File';
    return sourceKind || 'Unknown';
}

export default function CollectionMaterialEditorPage() {
    const {materialId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const {role, roles} = useSession() || {};
    const [material, setMaterial] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const m = await getCollectionMaterial(materialId);
            setMaterial(m);
        } catch (e) {
            setError(e?.message || 'Failed to load material');
            setMaterial(null);
        } finally {
            setLoading(false);
        }
    }, [materialId]);

    useEffect(() => {
        reload();
    }, [reload]);

    const handleSubmit = async () => {
        setBusy(true);
        setError(null);
        try {
            const m = await submitCollectionMaterial(materialId);
            setMaterial(m);
        } catch (e) {
            setError(e?.message || 'Failed to submit material');
        } finally {
            setBusy(false);
        }
    };

    const handleModify = async () => {
        setBusy(true);
        setError(null);
        try {
            const m = await modifyCollectionMaterial(materialId);
            setMaterial(m);
        } catch (e) {
            setError(e?.message || 'Failed to reopen material');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        setBusy(true);
        setError(null);
        try {
            await deleteCollectionMaterial(materialId);
            setDeleteConfirmOpen(false);
            handleBack();
        } catch (e) {
            setError(e?.message || 'Failed to delete material');
            setBusy(false);
        }
    };

    const handleCollectionMethodChange = async (collectionMethod) => {
        if (!material?.editable || collectionMethod === material.collectionMethod) return;
        setBusy(true);
        setError(null);
        try {
            const m = await updateCollectionMaterial(materialId, {collectionMethod});
            setMaterial(m);
        } catch (e) {
            setError(e?.message || 'Failed to update collection method');
        } finally {
            setBusy(false);
        }
    };

    const handleBack = () => {
        const returnTo = sanitizeReturnTo(location.state?.returnTo);
        if (returnTo) {
            navigate(returnTo);
            return;
        }
        navigate(collectionTaskPath({
            ctNumber: material?.ctNumber,
            id: material?.collectionTaskId,
        }));
    };

    if (loading) {
        return <Stack alignItems="center" py={6}><CircularProgress/></Stack>;
    }
    if (!material && error) {
        return <Box p={2}><Alert severity="error">{error}</Alert></Box>;
    }
    if (!material) {
        return <Box p={2}><Alert severity="error">Not found</Alert></Box>;
    }

    const readOnly = !material.editable;
    const canSubmit = Boolean(material.canSubmit);
    const canModify = Boolean(material.canModify);
    const canDelete = Boolean(material.canDelete);
    const collectionMethod = material.collectionMethod || 'OSINT';
    const isLegacyRichText = Boolean(material.reportId) && !material.dataItemId;
    const dataItemId = material.dataItemId != null ? String(material.dataItemId) : null;

    return (
        <Box
            p={2}
            mx="auto"
            sx={{
                maxWidth: 1200,
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* ── Top bar: Back (left) + actions (right) ── */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={1.5}>
                <Button
                    variant="outlined"
                    size="small"
                    sx={{whiteSpace: 'nowrap', minWidth: 'auto'}}
                    onClick={handleBack}
                >
                    Back to Task
                </Button>
                <Stack direction="row" spacing={1} flexShrink={0} sx={{whiteSpace: 'nowrap'}}>
                    {canSubmit && (
                        <Button
                            variant="contained"
                            color="success"
                            size="small"
                            disabled={busy}
                            onClick={handleSubmit}
                        >
                            Submit
                        </Button>
                    )}
                    {canModify && (
                        <Button variant="outlined" size="small" disabled={busy} onClick={handleModify}>
                            Modify
                        </Button>
                    )}
                    {canDelete && (
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            disabled={busy}
                            onClick={() => setDeleteConfirmOpen(true)}
                        >
                            Delete
                        </Button>
                    )}
                </Stack>
            </Stack>

            {/* ── Material identity — single compact line ── */}
            <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                mb={1.5}
                divider={
                    <Typography component="span" color="divider" sx={{userSelect: 'none', lineHeight: 1}}>
                        ·
                    </Typography>
                }
            >
                {/* CM number */}
                <Typography
                    variant="caption"
                    sx={{fontFamily: 'monospace', fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', whiteSpace: 'nowrap'}}
                >
                    {material.cmNumber || material.id}
                </Typography>

                {/* Title — omit for file materials with embedded viewer since the PDF
                    processor injects the same subject as an h1 at the top of the document */}
                {!(material.sourceKind === 'file' && dataItemId) && material.title && material.title !== material.cmNumber && (
                    <Typography variant="subtitle2" sx={{fontWeight: 600, lineHeight: 1.4}}>
                        {material.title}
                    </Typography>
                )}

                {/* Source type */}
                <Chip
                    size="small"
                    icon={<SourceKindIcon sourceKind={material.sourceKind}/>}
                    label={sourceKindLabel(material.sourceKind)}
                    variant="outlined"
                    sx={{height: 18, fontSize: '0.68rem', fontWeight: 600, '& .MuiChip-label': {px: 0.75}}}
                />

                {/* Collection method */}
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="caption" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>
                        Collection Method
                    </Typography>
                    {readOnly ? (
                        <Chip
                            size="small"
                            label={collectionMethod}
                            variant="outlined"
                            sx={{height: 18, fontSize: '0.68rem', '& .MuiChip-label': {px: 0.75}}}
                        />
                    ) : (
                        <FormControl size="small">
                            <Select
                                value={collectionMethod}
                                disabled={busy}
                                displayEmpty
                                sx={{
                                    fontSize: '0.78rem',
                                    '& .MuiSelect-select': {py: 0.3, px: 1},
                                    height: 24,
                                }}
                                onChange={(e) => handleCollectionMethodChange(e.target.value)}
                            >
                                {CM_COLLECTION_METHOD_OPTIONS.map((method) => (
                                    <MenuItem key={method} value={method} sx={{fontSize: '0.8rem'}}>{method}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{mb: 1.5}}>{error}</Alert>}

            <EntityTagsSection
                entityType="CM"
                entityId={materialId}
                canEdit={hasAnyRole(role, roles, CM_TAG_ROLES)}
                enabled={Boolean(materialId)}
                dense
            />

            {isLegacyRichText ? (
                <Alert severity="warning" sx={{mb: 1.5}}>
                    This material still uses the legacy rich-text report body. It will be migrated to a
                    DataItem automatically; open the task and re-import if content is missing.
                </Alert>
            ) : null}

            <Box
                sx={{
                    mt: 1.5,
                    flex: 1,
                    minHeight: 480,
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                }}
            >
                {dataItemId ? (
                    <Box sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
                        <SourcesFileViewerContent
                            fileId={dataItemId}
                            canonicalFileId={dataItemId}
                            fileName={material.title || material.cmNumber || 'Collected Material'}
                            fileType={material.mimeType || 'unknown'}
                            fileSource="collection_material"
                            hideToolbar
                        />
                    </Box>
                ) : (
                    <Stack flex={1} alignItems="center" justifyContent="center" p={3}>
                        <Alert severity="info" sx={{maxWidth: 480}}>
                            This material has no linked content yet.
                        </Alert>
                    </Stack>
                )}
            </Box>

            <Dialog open={deleteConfirmOpen} onClose={() => !busy && setDeleteConfirmOpen(false)}>
                <DialogTitle>Delete collected material?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This removes {material.cmNumber || 'this material'} and its linked uploaded
                        content from search. This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button disabled={busy} onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" disabled={busy} onClick={handleDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
